import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sidebar } from "./AdminDashboard";
import { RefreshCw, Search, Filter, ArrowUpDown, Activity, Loader2 } from "lucide-react";
import axios from "axios";
import { useAdminAuth, getAdminAuthHeaders } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransactionMonitor() {
  const { isLoading: authLoading, isAuthenticated, logout } = useAdminAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      fetchTransactions();
    }
  }, [page, statusFilter, isAuthenticated]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/admin/transactions?${params}`, {
        headers: getAdminAuthHeaders()
      });
      setTransactions(response.data.transactions);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'status-success',
      confirmed: 'status-success',
      pending: 'status-pending',
      cancelled: 'status-failed',
      failed: 'status-failed'
    };
    return <Badge className={`status-badge ${styles[status] || 'status-pending'}`}>{status}</Badge>;
  };

  const getSyncBadge = (syncStatus) => {
    const styles = {
      synced: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      failed: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[syncStatus] || styles.pending}>{syncStatus}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar onLogout={logout} />
      
      <main className="ml-64 p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-midnight font-outfit" data-testid="transactions-title">
              Transaction Monitor
            </h1>
            <p className="text-slate-600">Real-time booking transactions from all active plugins</p>
          </div>
          <Button 
            onClick={fetchTransactions} 
            variant="outline"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search by guest name or order ID..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Transaction Terminal */}
        <div className="transaction-terminal mb-6" data-testid="transaction-terminal">
          <div className="terminal-header">
            <div className="terminal-dot bg-red-500" />
            <div className="terminal-dot bg-yellow-500" />
            <div className="terminal-dot bg-green-500" />
            <span className="ml-4 text-slate-400 text-sm font-mono">Live Transaction Feed</span>
            <div className="ml-auto flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 live-indicator" />
              <span className="text-emerald-400 text-sm">Connected</span>
            </div>
          </div>
          <div className="terminal-content">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner border-slate-600 border-t-emerald-400" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No transactions yet</p>
                <p className="text-sm">Transactions will appear here as bookings are made</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-700">
                      <th className="text-left py-3 px-4 font-medium">Date/Time</th>
                      <th className="text-left py-3 px-4 font-medium">Order ID</th>
                      <th className="text-left py-3 px-4 font-medium">Guest</th>
                      <th className="text-left py-3 px-4 font-medium">Room</th>
                      <th className="text-left py-3 px-4 font-medium">Check-in</th>
                      <th className="text-left py-3 px-4 font-medium">Amount</th>
                      <th className="text-left py-3 px-4 font-medium">Fee (2%)</th>
                      <th className="text-left py-3 px-4 font-medium">Processor</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Sync</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    {transactions.map((tx, index) => (
                      <tr 
                        key={tx.id || index} 
                        className="border-b border-slate-700/50 transaction-row hover:bg-slate-800/30"
                        data-testid={`transaction-row-${index}`}
                      >
                        <td className="py-3 px-4 font-mono text-xs">{formatDate(tx.created_at)}</td>
                        <td className="py-3 px-4 font-mono">#{tx.woocommerce_order_id}</td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white">{tx.guest_name}</p>
                            <p className="text-xs text-slate-500">{tx.guest_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">{tx.room_type}</td>
                        <td className="py-3 px-4 font-mono text-xs">{tx.check_in}</td>
                        <td className="py-3 px-4 text-emerald-400 font-mono">
                          {formatCurrency(tx.booking_amount, tx.currency)}
                        </td>
                        <td className="py-3 px-4 text-amber-400 font-mono">
                          {formatCurrency(tx.fee_amount, tx.currency)}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-xs border-slate-600">
                            {tx.payment_processor === 'scotiabank_fiserv' ? 'Scotiabank' : 
                             tx.payment_processor === 'bob_plugnpay' ? 'Bank of the Bahamas' : 'Visa'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">{getStatusBadge(tx.status)}</td>
                        <td className="py-3 px-4">{getSyncBadge(tx.sync_status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="prev-page-btn"
              >
                Previous
              </Button>
              <Button 
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="next-page-btn"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
